import { useShopCart } from 'src/Libs/shop/hooks'
import useShopAdditionalProducts from '../hooks/use-shop-additional-products'
import useModals from 'src/Hooks/useModals'
import { ProductType, PowerMeterTypes, TransformerType } from '../types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getBasketNormalConfigurationIds, getBasketQntmConfigurationIds } from '../utils'
import { useShopCartContext } from '../context'
import { useShopChannelRules } from './use-shop-channel-type'

const INITIAL_QUANTITY = 1

interface UseShopCartUpdaterHook {
  isLoading: boolean
  addProductToCart: (
    productId: string,
    productType: string | undefined,
    productRequiresAdditionalProducts: boolean,
    minQtyOfAdditionalProducts: number | null,
    productHint: string | false,
    powerMeterType?: string,
    selectedTransformer?: string | null
  ) => Promise<void>
  updateProductToCart: (productId: string, quantity: number) => Promise<void>
  deleteProductFromCart: (productId: string) => Promise<void>
}

interface AddProductOptions {
  productId: string
  productType: string | undefined
  productRequiresAdditionalProducts: boolean
  minQtyOfAdditionalProducts: number | null
  productHint: string | false
  powerMeterType?: string
  selectedTransformer?: string | null
}

export const useShopCartUpdater = (): UseShopCartUpdaterHook => {
  const { addProductCart, deleteProductCart, changeProductCartQuantity, isLoading } = useShopCart()
  const { cartData, setCartData } = useShopCartContext()
  const { addAdditionalProductsToBasket } = useShopAdditionalProducts()
  const { requireRetrofitBatterySerialNumber } = useShopChannelRules()
  const {
    openShopTransformerModal,
    openShopRetrofitBatterySingleProduct,
    openShopLeavingConfirmation,
    openShopAdditionalProductsModal,
    openShopHintModal,
  } = useModals()
  const { t } = useCustomTranslation()

  const basketSystemConfigurationIds = getBasketNormalConfigurationIds(cartData)
  const basketQntmSystemConfigurationIds = getBasketQntmConfigurationIds(cartData)

  const addSingleProduct = async (productId: string): Promise<void> => {
    const result = await addProductCart(productId)
    setCartData(result)
  }

  /*
   * This function handle the logic of adding transformer for power meter products.
   */
  const handleTransformerProductAdd = async (productId: string, selectedTransformerId: string | string[]): Promise<void> => {
    const transformerItems = Array.isArray(selectedTransformerId)
      ? selectedTransformerId.map(id => ({ id, quantity: INITIAL_QUANTITY }))
      : [{ id: selectedTransformerId, quantity: INITIAL_QUANTITY }]

    const result = await addProductCart([{ id: productId, quantity: INITIAL_QUANTITY }, ...transformerItems])
    setCartData(result)
  }

  /*
   * This function shows a hint text modal.
   */
  const handleProductHint = (productType: string | undefined, productHint: string | false): void => {
    if (productType === ProductType.SmartMeter) {
      openShopHintModal({ text: t('shop.note.smart-meter-contract') })
      return
    }

    if (productHint) {
      openShopHintModal({ text: productHint })
    }
  }

  /*
   * This function handles logic normal products without any further action required.
   */
  const handleDefaultProduct = async (productId: string, productType: string | undefined, productHint: string | false): Promise<void> => {
    await addSingleProduct(productId)
    handleProductHint(productType, productHint)
  }

  /*
   * This function handles logic when a product requires additional product.
   */
  const handleAdditionalProducts = (productId: string, minQtyOfAdditionalProducts: number): void => {
    openShopAdditionalProductsModal({
      mainProductId: productId,
      minQtyOfAdditionalProducts,
      onConfirm: async (additionalProducts): Promise<void> => {
        const result = await addAdditionalProductsToBasket(productId, additionalProducts)
        if (result) setCartData(result.cartData)
      },
    })
  }

  /*
   * This function handles logic for power meter products and transformers.
   */
  const handlePowerMeter = async (productId: string, powerMeterType?: string, selectedTransformer?: string | null): Promise<void> => {
    if (powerMeterType === PowerMeterTypes.Other) {
      await addSingleProduct(productId)
      return
    }

    if (selectedTransformer) {
      const result = await addProductCart([
        { id: productId, quantity: INITIAL_QUANTITY },
        { id: selectedTransformer, quantity: INITIAL_QUANTITY },
      ])

      setCartData(result)
      return
    }

    openShopTransformerModal({
      transformerType: TransformerType.SingleProduct,
      id: null,
      powerMeterId: productId,
      onSingleProductAdd: async (selectedTransformerId: string | string[]): Promise<void> => {
        await handleTransformerProductAdd(productId, selectedTransformerId)
      },
    })
  }

  const checkRetrofitBatteryType = async (productId: string, isPowerMeterSerialNumberRequired: boolean): Promise<void> => {
    if (isPowerMeterSerialNumberRequired) {
      return openShopRetrofitBatterySingleProduct({ batteryId: productId })
    }

    await addSingleProduct(productId)
  }

  /*
   * This function handles logic for battery products.
   */
  const handleRetrofitBattery = async (productId: string): Promise<void> => {
    if (basketSystemConfigurationIds?.length) {
      return openShopLeavingConfirmation({
        title: t('shop.product.override-system-configurator'),
        text: t('shop.product.system-configurator-text'),
        confirmButtonText: t('shop.common.confirm'),
        cancelButtonText: t('shop.common.cancel'),
        onConfirm: async (onClose: () => void): Promise<void> => {
          await deleteProductCart(basketSystemConfigurationIds)
          onClose()
          await checkRetrofitBatteryType(productId, requireRetrofitBatterySerialNumber)
        },
      })
    }

    await checkRetrofitBatteryType(productId, requireRetrofitBatterySerialNumber)
  }

  /*
   * This function handles all the logic for different product types and configurators.
   */
  const handleProductAdd = async ({
    productId,
    productType,
    productRequiresAdditionalProducts,
    minQtyOfAdditionalProducts,
    productHint,
    powerMeterType,
    selectedTransformer,
  }: AddProductOptions): Promise<void> => {
    const isPowerMeter = productType === ProductType.PowerMeter && powerMeterType
    const isRetrofitBattery = productType === ProductType.RetrofitBattery

    if (productRequiresAdditionalProducts && minQtyOfAdditionalProducts) {
      return handleAdditionalProducts(productId, minQtyOfAdditionalProducts)
    }

    if (!isPowerMeter && !isRetrofitBattery) {
      return handleDefaultProduct(productId, productType, productHint)
    }

    if (isPowerMeter) {
      return handlePowerMeter(productId, powerMeterType, selectedTransformer)
    }

    if (isRetrofitBattery) {
      return handleRetrofitBattery(productId)
    }
  }

  /*
   * This function handles modal for overriding Quantum Configurator.
   */
  const handleQntmOverride = (qntmConfigurationIds: string[], options: AddProductOptions): void => {
    openShopLeavingConfirmation({
      title: t('shop.product.override-qntm-system-configurator'),
      text: t('shop.product.system-qntm-configurator-text'),
      confirmButtonText: t('shop.common.confirm'),
      cancelButtonText: t('shop.common.cancel'),
      onConfirm: async (onClose: () => void): Promise<void> => {
        const result = await deleteProductCart(qntmConfigurationIds)
        setCartData(result)
        onClose()

        await handleProductAdd(options)
      },
    })
  }

  /*
   * This hook handles cart products based on specific product type or requirements.
   */
  const addProductToCart = async (
    productId: string,
    productType: string | undefined,
    productRequiresAdditionalProducts: boolean,
    minQtyOfAdditionalProducts: number | null,
    productHint: string | false,
    powerMeterType?: string,
    selectedTransformer?: string | null
  ): Promise<void> => {
    const options: AddProductOptions = {
      productId,
      productType,
      productRequiresAdditionalProducts,
      minQtyOfAdditionalProducts,
      productHint,
      powerMeterType,
      selectedTransformer,
    }

    try {
      if (basketQntmSystemConfigurationIds?.length) {
        return handleQntmOverride(basketQntmSystemConfigurationIds, options)
      }

      await handleProductAdd(options)
    } catch (error) {
      console.error(error)
    }
  }
  /*
   * This hook handles the quantity change and updates cart
   */
  const updateProductToCart = async (productId: string, quantity: number): Promise<void> => {
    try {
      const result = await changeProductCartQuantity(productId, quantity)
      setCartData(result)
    } catch (error) {
      console.error(error)
    }
  }
  /*
   * This hook handles product delete from cart
   */
  const deleteProductFromCart = async (productId: string): Promise<void> => {
    try {
      const result = await deleteProductCart(productId)
      setCartData(result)
    } catch (error) {
      console.error(error)
    }
  }

  return {
    isLoading,
    addProductToCart,
    updateProductToCart,
    deleteProductFromCart,
  }
}
